import { DynamicForm } from "@shared/components/DynamicForm";
import { useActiveUtilityServices } from "@shared/hooks/useActiveUtililyServices";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { FormService } from "@shared/services/FormServices";
import { CheckCircle, Info } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useBasisTypeOptions,
  useCreateRateMutation,
  useRateTypeOptions,
  useUpdateRateMutation,
} from "../../../../hooks";
import { TariffPlan } from "./types";

import { toast } from "@shared/hooks/use-toast";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

// Rate type descriptions
const RATE_DESCRIPTIONS = {
  Flat: "A flat rate charges the same amount per unit of consumption regardless of how much is used.",
  Fixed:
    "A fixed rate maintains consistent pricing throughout the billing period.",
  Slab: "Slab rates apply different rates based on consumption ranges. Higher consumption may have different rates.",
  "Time Of Use":
    "Time of use rates vary based on the time of day when consumption occurs.",
  Seasonal:
    "Seasonal rates change based on different seasons or time periods throughout the year.",
};

interface TariffFormProps {
  onSubmit: (data: TariffPlan) => void | Promise<void>;
  onCancel?: () => void;
  isEdit?: boolean;
  loading?: boolean;
  className?: string;
}

export const TariffAddForm: React.FC<TariffFormProps> = ({
  onSubmit,
  onCancel,
  isEdit = false,
  loading = false,
  className = "",
}) => {
  // State management
  const [formLoading, setFormLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showValidation, setShowValidation] = useState(false);
  const [selectedRateType, setSelectedRateType] = useState<
    string | undefined
  >();
  const [selectedBasisType, setSelectedBasisType] = useState<
    string | undefined
  >();
  const [selectedRateTypeKey, setSelectedRateTypeKey] = useState<
    string | undefined
  >();

  // Hooks for fetching rate related data
  const { data: activeUtilityServices } = useActiveUtilityServices();
  const { data: ratyTypes } = useRateTypeOptions();
  const createRateMutation = useCreateRateMutation();
  const utilityId = useRemoteUtilityId();
  const currency = useCurrency();
  const { action } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { startTracking, stopTracking } = useFormCompletionTracker("Tariff");
  startTracking();

  const routeInitialData = location.state?.initialData;
  const isEditMode = action === "edit";
  const initialData = routeInitialData;
  const updateRateMutation = useUpdateRateMutation(initialData?.id);

  // Determine if we should fetch basis types (only for Variable rate type)
  const shouldFetchBasisTypes = useMemo(() => {
    if (selectedRateType === "Variable") {
      return selectedRateTypeKey;
    }
    if (
      isEditMode &&
      initialData?.rateTypeDisplay === "Variable" &&
      initialData?.rateType
    ) {
      return initialData.rateType;
    }
    return null;
  }, [selectedRateType, selectedRateTypeKey, isEditMode, initialData]);

  // Call useBasisTypeOptions with null when not needed
  // The hook should handle null values by not making the API call
  const { data: basisTypes } = useBasisTypeOptions(shouldFetchBasisTypes);

  console.log("Initial data from route:", JSON.stringify(initialData, null, 2));

  const parseFlexibleDateToString = (input) => {
    if (!input) return "";

    const knownFormats = [
      "YYYY-MM-DD",
      "DD MMM YYYY",
      "MMM DD YYYY",
      "DD/MM/YYYY",
      "MM/DD/YYYY",
      "YYYY/MM/DD",
      "DD-MM-YYYY",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DDTHH:mm:ssZ",
      "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ (z)",
    ];

    for (const format of knownFormats) {
      const date = dayjs(input, format, true);
      if (date.isValid()) return date.format("YYYY-MM-DD");
    }

    const fallback = dayjs(input); // try native parse
    return fallback.isValid() ? fallback.format("YYYY-MM-DD") : "";
  };

  // Transform initial data for form consumption
  const transformedInitialData = useMemo(() => {
    if (!initialData) return null;

    console.log("🔄 Transforming initial data for form...");

    // Find the rate type display value
    const rateTypeDisplay =
      initialData.rateTypeDisplay ||
      ratyTypes?.find((rt) => rt.key === initialData.rateType)?.value;

    const basisTypeDisplay = initialData.basisType || "";

    console.log(
      "Rate type and basis type from data:",
      initialData.rateType,
      initialData.basisType,
      "Display:",
      rateTypeDisplay
    );

    // Transform rate data based on rate type
    let transformedRates: any = {};

    if (
      rateTypeDisplay === "Variable" &&
      basisTypeDisplay === "Seasonal" &&
      initialData.rate
    ) {
      transformedRates.seasonalRates = initialData.rate.map((rate: any) => ({
        season_name: rate.seasonName,
        start_time: rate.startTime,
        end_time: rate.endTime,
        rate: rate.rate,
      }));
    } else if (
      rateTypeDisplay === "Variable" &&
      basisTypeDisplay === "Slab" &&
      initialData.rate
    ) {
      transformedRates.slabRates = initialData.rate.map((rate: any) => ({
        start_time: rate.startTime || rate.start_time || 0,
        end_time: rate.endTime || rate.end_time || 0,
        rate: rate.unitSlabRate || rate.unit_slab_rate || rate.rate || 0,
      }));
    } else if (
      rateTypeDisplay === "Variable" &&
      basisTypeDisplay === "Time Of Use" &&
      initialData.rate
    ) {
      transformedRates.timeOfUseRates = initialData.rate.map((rate: any) => ({
        start_time: rate.startTime || rate.start_time || "00:00",
        end_time: rate.endTime || rate.end_time || "23:59",
        rate:
          rate.unitTimeOfUseRate ||
          rate.unit_time_of_use_rate ||
          rate.rate ||
          0,
      }));
    } else if (
      rateTypeDisplay === "Variable" &&
      basisTypeDisplay === "Flat" &&
      initialData.rate
    ) {
      transformedRates.unitRate = initialData.rate[0]["rate"] || 0;
    } else if (
      rateTypeDisplay === "Fixed" &&
      initialData.rate &&
      initialData.rate[0]
    ) {
      transformedRates.fixedRate = initialData.rate[0]["rate"] || 0;
    }

    console.log("Transformed rates:", transformedRates);

    const transformed = {
      rate_name: initialData.rateName ?? "",
      utilityType: initialData.utilityService ?? "",
      rateType: initialData.rateType ?? "",
      basisType:
        basisTypes?.find((rt) => rt.value === initialData.basisType)?.key ?? "",
      validFrom: parseFlexibleDateToString(initialData.effectiveDate),
      validTo: parseFlexibleDateToString(initialData.expiryDate),
      fixedRate: 0,
      unitRate: 0,
      slabRates: [],
      timeOfUseRates: [],
      seasonalRates: [],
      ...transformedRates,
    };

    console.log("✅ Transformed data:", transformed);
    return transformed;
  }, [initialData, ratyTypes, basisTypes]);

  // Set selectedRateType when we have the data
  useEffect(() => {
    if (isEditMode && initialData) {
      const rateTypeDisplay =
        initialData.rateTypeDisplay ||
        ratyTypes?.find((rt) => rt.key === initialData.rateType)?.value;

      const basisTypeDisplay = initialData.basisType || "";

      console.log("🎯 Setting selectedRateType to:", rateTypeDisplay);
      setSelectedRateType(rateTypeDisplay);
      setSelectedBasisType(basisTypeDisplay);
      setSelectedRateTypeKey(initialData.rateType);
    }
  }, [isEditMode, initialData, ratyTypes, basisTypes]);

  const utilityOptions = useMemo(() => {
    return activeUtilityServices
      ? activeUtilityServices.map((service) => ({
          label: service.name,
          value: service.name,
        }))
      : [];
  }, [activeUtilityServices]);

  const basisTypeOption = useMemo(() => {
    return basisTypes
      ? basisTypes.map((item) => ({
          label: item.value,
          value: item.key,
        }))
      : [];
  }, [basisTypes]);

  const rateTypeOptions = useMemo(() => {
    return ratyTypes
      ? ratyTypes.map((item) => ({
          label: item.value,
          value: item.key,
        }))
      : [];
  }, [ratyTypes]);

  // Base field definitions
  const createBaseFields = useCallback(
    () => [
      {
        name: "rate_name",
        label: "Tariff Name",
        type: "text" as const,
        required: true,
        placeholder: "Enter tariff name",
        fullWidth: true,
        helperText:
          "Choose a descriptive name that identifies this tariff plan.",
        group: "Basic Information",
        groupColor: "bg-blue-50",
        groupOrder: 1,
        minLength: 3,
        maxLength: 100,
        classes: {
          container: "w-full mb-6 col-span-2",
          label: "block text-sm font-medium text-gray-700 ",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white text-gray-900",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
      {
        name: "utilityType",
        label: "Utility Type",
        type: "select" as const,
        required: true,
        placeholder: "Select utility type",
        options: utilityOptions,
        group: "Basic Information",
        groupOrder: 2,
        showHelperTooltip: true,
        tooltipText: "Select the type of utility this tariff applies to",
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 ",
          select:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white text-gray-900 cursor-pointer",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
      {
        name: "rateType",
        label: "Rate Type",
        type: "select" as const,
        required: true,
        placeholder: "Select rate type",
        options: rateTypeOptions,
        group: "Basic Information",
        groupOrder: 3,
        showHelperTooltip: true,
        tooltipText: "Choose the pricing structure for this tariff",
        disabled: isEditMode, // ✅ disable the field
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 ",
          select: `w-full h-12 px-4 border border-gray-300 rounded-lg 
  focus:border-blue-500 focus:ring-2 focus:ring-blue-200 
  transition-all duration-200 bg-white text-gray-900 
  ${isEditMode ? "cursor-not-allowed bg-gray-100" : "cursor-pointer"}`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
      {
        name: "validFrom",
        label: "Valid From",
        type: "datepicker" as const,
        required: true,
        helperText: "The date when this tariff becomes active.",
        group: "Validity Period",
        groupOrder: 1,
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 ",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white text-gray-900",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
      {
        name: "validTo",
        label: "Valid To (Optional)",
        type: "datepicker" as const,
        required: false,
        placeholder: "Select end date (optional)",
        helperText:
          "The date when this tariff expires. Leave empty for indefinite validity.",
        group: "Validity Period",
        groupColor: "bg-blue-50",

        groupOrder: 2,
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 ",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white text-gray-900",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
      },
    ],
    [utilityOptions, rateTypeOptions]
  );

  // Rate-specific field definitions
  const createBasisFields = useCallback(
    () => ({
      unitRate: {
        name: "unitRate",
        label: "Unit Rate",
        type: "decimal" as const,
        required: false,
        placeholder: "0.00",
        fullWidth: true,
        helperText:
          "Enter the per-unit cost that will be applied to all consumption.",
        group: "Rate Configuration",
        groupOrder: 1,
        // prefix: currency || "$",r
        min: 0,
        step: 0.01,
        classes: {
          container: "w-full mb-6 col-span-2",
          label: "block text-sm font-medium text-gray-700 ",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white text-gray-900",
          error: "text-red-600 text-sm mt-1",
        },
      },
      slabRates: {
        name: "slabRates",
        label: "Slab Rate Configuration",
        type: "form-array" as const,
        fullWidth: true,
        required: false,
        group: "Rate Configuration",
        groupOrder: 1,
        groupColor: "bg-blue-50",
        helperText: "Define consumption slabs with different rates.",
        minItems: 1,
        addButtonText: "Add Slab Rate",
        emptyMessage: "No slab rates defined yet. Add at least one slab rate.",
        showAddButton: true,
        showRemoveButton: true,
        itemFields: [
          {
            name: "start_time",
            label: "From",
            type: "number" as const,
            required: true,
            min: 0,
            placeholder: "0",
          },
          {
            name: "end_time",
            label: "To",
            type: "number" as const,
            required: true,
            min: 0,
            placeholder: "100",
          },
          {
            name: "rate",
            label: `Rate`,
            type: "decimal" as const,
            required: true,
            min: 0,
            step: 0.001,
            placeholder: "0.00",
            //// prefix: currency || "$",r
          },
        ],
        classes: {
          container: "w-full col-span-2",
          // label: "block text-sm font-medium text-gray-700 ",
          // error: "text-red-600 text-sm mt-1",
        },
      },
      timeOfUseRates: {
        name: "timeOfUseRates",
        label: "Time of Use Rate Configuration",
        type: "form-array" as const,
        required: false,
        fullWidth: true,
        group: "Rate Configuration",
        groupOrder: 1,
        helperText: "Define different rates for different times of day.",
        minItems: 1,
        addButtonText: "Add Time Period",
        emptyMessage:
          "No time periods defined yet. Add at least one time period.",
        showAddButton: true,
        showRemoveButton: true,
        classes: {
          container: "w-full mb-6 col-span-2",
          label: "block text-sm font-medium text-gray-700 ",
          error: "text-red-600 text-sm mt-1",
        },
        itemFields: [
          {
            name: "start_time",
            label: "Start Time",
            type: "time" as const,
            required: true,
            placeholder: "00:00",
          },
          {
            name: "end_time",
            label: "End Time",
            type: "time" as const,
            required: true,
            placeholder: "23:59",
          },
          {
            name: "rate",
            label: `Rate`,
            type: "decimal" as const,
            required: true,
            min: 0,
            step: 0.001,
            placeholder: "0.00",
            // prefix: currency || "$",r
          },
        ],
      },
      seasonalRates: {
        name: "seasonalRates",
        label: "Seasonal Rate Configuration",
        type: "form-array" as const,
        required: false,
        group: "Rate Configuration",
        fullWidth: true,
        groupOrder: 1,
        helperText: "Define different rates for different seasons.",
        minItems: 1,
        addButtonText: "Add Season",
        emptyMessage:
          "No seasonal periods defined yet. Add at least one season.",
        showAddButton: true,
        showRemoveButton: true,
        classes: {
          container: "w-full mb-6 col-span-2",
          label: "block text-sm font-medium text-gray-700 ",
          error: "text-red-600 text-sm mt-1",
        },
        itemFields: [
          {
            name: "season_name",
            label: "Season Name",
            type: "text" as const,
            required: true,
            placeholder: "e.g., Summer, Winter",
          },
          {
            name: "start_time",
            label: "Start Date",
            type: "datepicker" as const,
            required: true,
          },
          {
            name: "end_time",
            label: "End Date",
            type: "datepicker" as const,
            required: true,
          },
          {
            name: "rate",
            label: `Rate`,
            type: "decimal" as const,
            required: true,
            min: 0,
            step: 0.001,
            placeholder: "0.00",
            // prefix: currency || "$",r
          },
        ],
      },
    }),
    [currency]
  );

  const createRateFields = useCallback(
    () => ({
      variableRate: {
        name: "basisType",
        label: "Basis Type",
        type: "select" as const,
        required: false,
        placeholder: "Select basis type",
        options: basisTypeOption,
        group: "Basic Information",
        groupOrder: 3,
        disabled: isEditMode,
        showHelperTooltip: true,
        tooltipText: "Choose the pricing structure for this tariff",
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 ",
          select:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white text-gray-900 cursor-pointer",
          error: "text-red-600 text-sm mt-1",
        },
      },
      fixedRate: {
        name: "fixedRate",
        label: "Fixed Rate",
        type: "decimal" as const,
        required: true,
        placeholder: "0.00",
        fullWidth: true,
        helperText:
          "Enter the fixed cost that will be applied irrespective of consumption.",
        group: "Rate Configuration",
        groupOrder: 1,

        classes: {
          container: "w-full mb-6 col-span-2",
          label: "block text-sm font-medium text-gray-700 ",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white text-gray-900",
          error: "text-red-600 text-sm mt-1",
        },
      },
    }),
    [currency, basisTypeOption]
  );

  // Generate fields based on selected rate type
  const generateFormFields = useCallback(
    (selectedRateType?: string) => {
      const baseFields = createBaseFields();
      const rateFields = createRateFields();

      // Add rate-specific fields based on selection
      const dynamicFields = [];

      if (selectedRateType === "Fixed") {
        dynamicFields.push({ ...rateFields.fixedRate, required: true });
      } else if (selectedRateType === "Variable") {
        rateFields.variableRate["required"] = true;
        dynamicFields.push({ ...rateFields.variableRate, required: true });
      }

      return [...baseFields, ...dynamicFields];
    },
    [createBaseFields, createRateFields]
  );

  // Generate fields based on selected rate type
  const geneBasisFormFields = useCallback(
    (selectedBasisType?: string) => {
      const basisFields = createBasisFields();

      // Add rate-specific fields based on selection
      const dynamicFields = [];

      if (selectedBasisType === "Flat") {
        dynamicFields.push({ ...basisFields.unitRate, required: true });
      } else if (selectedBasisType === "Slab") {
        dynamicFields.push({ ...basisFields.slabRates, required: true });
      } else if (selectedBasisType === "Time Of Use") {
        dynamicFields.push({ ...basisFields.timeOfUseRates, required: true });
      } else if (selectedBasisType === "Seasonal") {
        dynamicFields.push({ ...basisFields.seasonalRates, required: true });
      }

      return [...dynamicFields];
    },
    [createBasisFields]
  );

  // Create form configuration with ALL possible fields (for form registration)
  const allPossibleFields = useMemo(() => {
    const baseFields = createBaseFields();
    const rateFields = createRateFields();
    const basisFields = createBasisFields();

    return [
      ...baseFields,
      rateFields.variableRate,
      rateFields.fixedRate,
      basisFields.unitRate,
      basisFields.slabRates,
      basisFields.timeOfUseRates,
      basisFields.seasonalRates,
    ];
  }, [createBaseFields, createRateFields, createBasisFields]);

  // Default values for form
  // const defaultValues = useMemo(
  //   () => ({
  //     rate_name: transformedInitialData?.rate_name || "",
  //     utilityType: transformedInitialData?.utilityType || "",
  //     rateType: transformedInitialData?.rateType || "",
  //     validFrom: transformedInitialData?.validFrom || "",
  //     validTo: transformedInitialData?.validTo || "",
  //     fixedRate: transformedInitialData?.fixedRate || 0,
  //     unitRate: transformedInitialData?.unitRate || 0,
  //     slabRates: transformedInitialData?.slabRates || [],
  //     timeOfUseRates: transformedInitialData?.timeOfUseRates || [],
  //     seasonalRates: transformedInitialData?.seasonalRates || [],
  //   }),
  //   [transformedInitialData]
  // );

  const defaultValues = useMemo(
    () =>
      isEditMode
        ? {
            rate_name: transformedInitialData?.rate_name || "",
            utilityType: transformedInitialData?.utilityType || "",
            rateType: transformedInitialData?.rateType || "",
            validFrom: transformedInitialData?.validFrom || "",
            validTo: transformedInitialData?.validTo || "",
            fixedRate: transformedInitialData?.fixedRate || 0,
            unitRate: transformedInitialData?.unitRate || 0,
            slabRates: transformedInitialData?.slabRates || [],
            timeOfUseRates: transformedInitialData?.timeOfUseRates || [],
            seasonalRates: transformedInitialData?.seasonalRates || [],
          }
        : undefined,
    [transformedInitialData, isEditMode]
  );

  // Create form instance with ALL possible fields
  const form: UseFormReturn<any> = FormService.createForm({
    fields: allPossibleFields,
    defaultValues,
    mode: "onBlur" as const,
    layout: "vertical" as const,
    spacing: "comfortable" as const,
    showProgress: true,
    showResetButton: false,
    showSubmitButton: false,
  });

  // Get current fields to display
  const currentFields = useMemo(() => {
    if (!utilityOptions.length) return [];
    if (selectedRateType == "Variable")
      return [
        ...generateFormFields(selectedRateType),
        ...geneBasisFormFields(selectedBasisType),
      ];
    return generateFormFields(selectedRateType);
  }, [
    utilityOptions,
    selectedRateType,
    generateFormFields,
    geneBasisFormFields,
    selectedBasisType,
  ]);

  // Populate form for edit mode
  useEffect(() => {
    if (isEditMode && transformedInitialData && allPossibleFields.length > 0) {
      console.log(
        "🔄 Populating form for edit mode with transformed data:",
        transformedInitialData
      );

      // Use a slight delay to ensure form is ready
      setTimeout(() => {
        Object.entries(transformedInitialData).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            console.log(`Setting form field ${key} to:`, value);
            form.setValue(key, value);
          }
        });

        // Force update form state
        form.trigger();
      }, 200);
    }
  }, [isEditMode, transformedInitialData, allPossibleFields, form]);

  // Transform form data to API payload
  const transformToApiPayload = (
    formData: any,
    selectedRateType: string,
    selectedBasisType?: string
  ): any => {
    console.log("--- TRANSFORM TO API PAYLOAD ---");
    console.log("Input form data:", formData);
    console.log("Selected rate type:", selectedRateType);

    const payload: any = {
      rate_name: formData.rate_name,
      utility_service: formData.utilityType,
      rate_type: formData.rateType,
      basis_type: basisTypes?.find(
        (rt) => rt.key === Number(formData.basisType)
      )?.value,
      effective_date: formData.validFrom,
      expiry_date: formData.validTo || undefined,
      currency: currency,
      remote_utility_id: utilityId,
    };

    // Add rate-specific data
    switch (selectedRateType) {
      case "Variable":
        switch (selectedBasisType) {
          case "Flat":
            payload.rate = [
              {
                start_time: 0,
                end_time: 0,
                rate: parseFloat(formData.unitRate) || 0,
              },
            ];
            break;
          case "Slab":
            payload.rate = Array.isArray(formData.slabRates)
              ? formData.slabRates
              : [];
            break;
          case "Time Of Use":
            payload.rate = Array.isArray(formData.timeOfUseRates)
              ? formData.timeOfUseRates
              : [];
            break;
          case "Seasonal":
            payload.rate = Array.isArray(formData.seasonalRates)
              ? formData.seasonalRates
              : [];
            break;
        }
        break;
      case "Fixed":
        payload.rate = [
          {
            start_time: 0,
            end_time: 0,
            rate: parseFloat(formData.fixedRate) || 0,
          },
        ];
        break;
    }

    console.log("Final transformed payload:", payload);
    return payload;
  };

  // Business rules validation
  const validateTariffData = (
    data: any
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    console.log("--- VALIDATE TARIFF DATA ---");
    console.log("Input data for validation:", data);

    // Date validation
    if (
      data.expiry_date &&
      new Date(data.expiry_date) <= new Date(data.effective_date)
    ) {
      errors.push("Valid To date must be after Valid From date");
      toast({
        title: "Error",
        description: "Valid To date must be after Valid From date",
        variant: "destructive",
      });
    }

    // Rate-specific validation
    switch (selectedRateType) {
      case "Varialble":
        switch (selectedBasisType) {
          case "Flat":
            if (!data.rate || data.rate.length === 0) {
              errors.push("Unit rate is required for Flat rate type");
            } else if (!data.rate[0].rate || data.rate[0].rate <= 0) {
              errors.push(
                "Unit rate must be greater than 0 for Flat rate type"
              );
            }
            break;
          case "Slab":
            if (!data.rate || data.rate.length === 0) {
              errors.push("At least one slab rate is required");
            }
            break;
          case "Time Of Use":
            if (!data.rate || data.rate.length === 0) {
              errors.push("At least one time period is required");
            }
            break;
          case "Seasonal":
            if (!data.rate || data.rate.length === 0) {
              errors.push("At least one seasonal rate is required");
            }
            break;
        }
        break;
      case "Fixed":
        if (
          !Array.isArray(data.rate) ||
          data.rate.length !== 1 ||
          !data.rate[0].rate ||
          data.rate[0].rate <= 0
        ) {
          errors.push("Fixed rate must be greater than 0");
        }
        break;
    }

    return { isValid: errors.length === 0, errors };
  };

  // Main form submission handler
  const handleSubmit = async (data: any) => {
    console.log("=== FORM SUBMISSION START ===");
    console.log("Raw form data:", data);

    if (!selectedRateType) {
      console.log("No rate type selected");
      setValidationErrors(["Please select a rate type"]);
      setShowValidation(true);
      return;
    }

    const payload = transformToApiPayload(
      data,
      selectedRateType,
      selectedBasisType
    );
    console.log("Transformed payload:", payload);
    const validation = validateTariffData(payload);

    if (!validation.isValid) {
      console.log("Validation failed:", validation.errors);
      setValidationErrors(validation.errors);
      setShowValidation(true);
      return;
    }

    setValidationErrors([]);
    setShowValidation(false);
    setFormLoading(true);

    const mutationFn = isEditMode ? updateRateMutation : createRateMutation;

    mutationFn.mutate(payload, {
      onSuccess: async () => {
        stopTracking({
          action: isEditMode ? "EDIT" : "ADD",
          ...payload,
          rate_type: selectedRateType,
        });
        console.log("=== FORM SUBMISSION SUCCESS ===");
        form.reset();
        navigate("/plan-tarrif/plan-tarrif-landing?tab=tariff");
      },
      onError: (error) => {
        console.error("Error saving tariff:", error);
      },
      onSettled: () => {
        setFormLoading(false);
      },
    });
  };

  // Handle field changes
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      console.log(`🔄 Field ${fieldName} changed to:`, value);

      // Clear validation errors on any field change
      if (showValidation) {
        setShowValidation(false);
        setValidationErrors([]);
      }

      // Handle rateType key-to-value mapping
      if (fieldName === "rateType") {
        console.log("Rate type field changed to:", value);
        const selected = ratyTypes?.find(
          (rt) => rt.key === Number(value)
        )?.value;
        console.log("Selected Rate Type (mapped):", selected);
        setSelectedRateType(selected);
        // Set the key for basis type fetching
        setSelectedRateTypeKey(value);
      }

      // Handle basisType key-to-value mapping
      if (fieldName === "basisType") {
        console.log("Basis type field changed to:", value);
        const selected = basisTypes?.find(
          (rt) => rt.key === Number(value)
        )?.value;
        console.log("Selected Basis Type (mapped):", selected);
        setSelectedBasisType(selected);
      }
    },
    [showValidation, ratyTypes, basisTypes]
  );

  // Handle form cancel/reset
  const handleCancel = useCallback(() => {
    form.reset();
    setValidationErrors([]);
    setShowValidation(false);
    navigate("/plan-tarrif/plan-tarrif-landing?tab=tariff");
  }, [form, onCancel]);

  // Custom footer with validation and actions
  const renderCustomFooter = useCallback(
    () => (
      <div className="space-y-4">
        {/* 
        =====================PLEASE DON'T REMOVE BELOW COMMNETED CODE OF DEBUG SECTION IT WILL BENEFICIAL FOR DEBUGING CASE RATHER GOING THROUGH ALL CODE========================== */}

        {/* Debug info for edit mode */}
        {/* {isEditMode && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Edit Mode:</strong> Selected Rate Type:{" "}
              {selectedRateType || "None"} | Form Values:{" "}
              {JSON.stringify(form.getValues(), null, 2).substring(0, 100)}...
            </p>
          </div>
        )} */}

        {/* Validation Errors */}
        {/* {showValidation && validationErrors.length > 0 && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-red-800">
                  Validation Errors
                </h4>
                <ul className="mt-2 space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index} className="text-sm text-red-700">
                      • {error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )} */}

        {/* Rate Type Information */}
        {((selectedRateType && RATE_DESCRIPTIONS[selectedRateType]) ||
          (selectedBasisType && RATE_DESCRIPTIONS[selectedBasisType])) && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-blue-800">
                  About{" "}
                  {!(selectedRateType === "Variable")
                    ? selectedRateType
                    : selectedBasisType}{" "}
                  Rate
                </h4>
                <p className="mt-1 text-sm text-blue-700">
                  {RATE_DESCRIPTIONS[selectedRateType] ||
                    RATE_DESCRIPTIONS[selectedBasisType]}
                </p>
                {selectedRateType === "Flat" && (
                  <p className="mt-2 text-sm text-blue-600">
                    <strong>Example:</strong> A flat rate of {currency || "$"}
                    0.12 per means all electricity usage is charged at the same
                    rate.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Note about date overlaps */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md mt-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Dates cannot overlap with existing tariffs of
            the same utility and rate type.
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            disabled={formLoading || loading}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={formLoading || loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
          >
            {(formLoading || loading) && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {formLoading || loading ? (
              "Saving..."
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                {isEditMode ? "Update & Publish" : "Save & Publish"}
              </>
            )}
          </button>
        </div>
      </div>
    ),
    [
      showValidation,
      validationErrors,
      selectedRateType,
      selectedBasisType,
      formLoading,
      loading,
      isEditMode,
      handleCancel,
      form,
      currency,
    ]
  );

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div>
        <p className="page-title">
          {isEditMode ? "Edit Tariff Plan" : "Create New Tariff Plan"}
        </p>
        <p className="page-subtitle">
          Configure your utility tariff plan with rates, validity periods, and
          billing structure.
        </p>
      </div>

      {/* Main Form Container */}

      <DynamicForm
        fields={currentFields}
        form={form}
        onSubmit={handleSubmit}
        loading={formLoading || loading}
        onFieldChange={handleFieldChange}
        renderCustomFooter={renderCustomFooter}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2, lg: 2, xl: 2 }}
        config={{
          layout: "vertical",
          spacing: "comfortable",
          showResetButton: false,
          showSubmitButton: false,
        }}
      />
    </div>
  );
};
